/* global G, PLATFORM */
import { asyncPipeSpread, setKey } from 'lib/util'
import refresh from 'lib/sequence/model/api/refresh'
import decorate from 'lib/sequence/model/decorator'
import map from 'lib/sequence/model/api/map'
import find from 'lib/sequence/component/children/find'
import { hide, show } from 'lib/sequence/component/state/hidden'

/**
 * Sets the id and token in the tabs' state for later use.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setRef = module => async (components, ...args) => {
  const [id, token] = module[G.ADAPTER][G.SESSION][G.STATE][G.REF].split('/')
  const { tabs } = components

  const data = { id, token }
  setKey(data, 'data', tabs[G.STATE])

  return [data, components, ...args]
}

/**
 * Fetches the person document
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {(function(*, *, ...[*]): Promise<*>)|*}
 */
const fetchData = module => async (data, components, ...args) => {
  const httpAdapter = module[G.MODEL][G.ADAPTER][G.HTTP]
  const { version } = module[G.MODEL][G.PROPS]

  try {
    const response = await httpAdapter.get(
      {
        url: `/api/v${version}/public/register/${data.id}`,
        params: { token: data.token },
      },
      { middleware: ({ loader, error, invite }) => [loader, error, invite] },
    )

    return [response?.person, components, ...args]
  } catch (e) {
    console.error(e)
  }

  return [null, components, ...args]
}

/**
 * Maps the person data to the form.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, *, ...[*]): Promise<*[]>}
 */
const mapData = module => async (person, components, ...args) => {
  if (!person) return [components, ...args]

  const { tabs, form } = components

  setKey(
    person?.value?.invite?.role,
    'role',
    tabs[G.STATE],
  )
  const model = module[G.MODEL]
  const { person: personModel } = model[G.CHILDREN]
  const { contactChannels } = personModel[G.CHILDREN]
  const { firstName, lastName } = person.value || {}
  
  // Fill the model's G.CACHE from the response.
  await refresh(personModel, {
    ...person,
    value: {
      ...person.value,
      firstName: firstName === '-' ? '' : firstName,
      lastName: lastName === '-' ? '' : lastName,
    },
  })

  // We need to execute `fromContactChannels` decorator to map the individual channels to each
  // `G.CHILDREN`'s `G.STATE.value`. Otherwise, email and password won't show up in the form.
  decorate(personModel)

  // We have saturated the model with response we got from the server. It now has `G.CACHE` set
  // properly. Let's map stuff.
  map(personModel)(form)
  map(contactChannels)(form)

  return [components, ...args]
}

/**
 * Toggles the correct action for the current active tab.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
export const toggleActions = module => async (components, ...args) => {
  if (PLATFORM === 'mobile') {
    const { tabs } = components
    const { activeTab } = tabs[G.STATE]
    const actions = module[G.STATE][G.ACTION][G.COMPONENT][G.ACTIONS] || []
    const btnContinue = actions.find(action => action[G.PROPS].key === 'btnContinueAction')
    const btnConfirm = actions.find(action => action[G.PROPS].key === 'btnConfirmAction')

    if (activeTab === 'contact') {
      hide(btnContinue)
      show(btnConfirm)
    } else {
      show(btnContinue)
      hide(btnConfirm)
    }
  }

  return [components, ...args]
}

/**
 * Registration Invite Action
 *
 * @param {Gaia.AppModule.Spec} module
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  setRef(module),
  fetchData(module),
  mapData(module),
  toggleActions(module),
)(find(component), ...args)
